import React, { useState, useEffect } from 'react';
import util from '../../utils/util';
import {
    Card, Table, Button, Modal, Select, Input, DatePicker,
    Row, Col, message, Tag, Divider, Checkbox
} from 'antd';
import {
    TeamOutlined, PlusOutlined, EditOutlined, DeleteOutlined,
    ReloadOutlined, UserOutlined
} from '@ant-design/icons';
import QoCService from '../../services/QoCService';
import FacilityService from '../../services/FacilityService';
import { usePeriods } from '../../hooks/usePeriods';
import axios from '../../utils/axios';
import moment from 'moment';
import { trailCreate, trailUpdate, trailDelete, TRAIL_MODULES } from '../../utils/activityTrail';
const { Option } = Select;
const { TextArea } = Input;

const ROLES = ['Lead Auditor','Auditor','Technical Expert','Observer','National Office Representative'];
const DEPTS = ['OPD','Pharmacy','Laboratory','Maternity','Radiology','Wards','ANC','ARV Clinic',
               'TB Clinic','Emergency','Theatre','Stores','Admin','Management'];

export default function QoCTeamRegistry() {
    const { periods, currentPeriod } = usePeriods();
    const [teams, setTeams]         = useState([]);
    // Permission checks
    const canAdd    = util.canAdd('qoc_team');
    const canEdit   = util.canEdit('qoc_team');
    const canDelete = util.canDelete('qoc_team');
    const canExport = util.canExport('qoc_team');
    const [facilities, setFacilities] = useState([]);
    const [users, setUsers]         = useState([]);
    const [loading, setLoading]     = useState(false);
    const [modal, setModal]         = useState(null);
    const [form, setForm]           = useState({});
    const [saving, setSaving]       = useState(false);
    const [filters, setFilters]     = useState({ facility_id:'', period_label:'' });

    useEffect(() => {
        FacilityService.all({ status:1 }).then(r =>
            setFacilities(r.data?.result?.data || []));
        axios.get('users').then(r =>
            setUsers(r.data?.result?.data || []));
    }, []);

    useEffect(() => {
        if (currentPeriod && !filters.period_label)
            setFilters(f => ({ ...f, period_label: currentPeriod.label }));
    }, [currentPeriod]); // eslint-disable-line

    // eslint-disable-next-line
    useEffect(() => { load(); }, [filters]);

    const load = async () => {
        setLoading(true);
        try {
            const res = await QoCService.getAuditTeam({
                ...(filters.facility_id  && { facility_id: filters.facility_id }),
                ...(filters.period_label && { period_label: filters.period_label }),
            });
            setTeams(res.data?.data || []);
        } catch { message.error('Failed to load'); }
        finally { setLoading(false); }
    };

    const openModal = (team = null) => {
        if (team) {
            setForm({
                ...team,
                members: team.members || [],
                departments_visited: team.departments_visited || [],
            });
        } else {
            setForm({
                members: [{ name:'', role:'Lead Auditor', user_id:'' }],
                departments_visited: [],
                period_label: currentPeriod?.label || '',
            });
        }
        setModal(team || {});
    };

    const addMember = () =>
        setForm(f => ({ ...f, members: [...(f.members||[]), { name:'', role:'Auditor', user_id:'' }] }));

    const updateMember = (i, field, val) =>
        setForm(f => {
            const m = [...(f.members||[])];
            m[i] = { ...m[i], [field]: val };
            // Auto-fill name from user selection
            if (field === 'user_id' && val) {
                const u = users.find(u => u.id === val);
                if (u) m[i].name = u.name;
            }
            return { ...f, members: m };
        });

    const removeMember = (i) =>
        setForm(f => ({ ...f, members: f.members.filter((_,idx) => idx !== i) }));

    const handleSave = async () => {
        if (!form.facility_id) { message.error('Facility is required'); return; }
        if (!form.audit_date)  { message.error('Audit date is required'); return; }
        setSaving(true);
        try {
            await QoCService.saveAuditTeam(form);

            const facilityName = facilities.find(f => f.id === form.facility_id)?.business_name || String(form.facility_id);
            form.id
                ? trailUpdate(TRAIL_MODULES.AUDIT_TEAM, facilityName, form.id,
                    { period: form.period_label, memberCount: (form.members||[]).length, date: form.audit_date })
                : trailCreate(TRAIL_MODULES.AUDIT_TEAM, facilityName, '',
                    { period: form.period_label, memberCount: (form.members||[]).length, date: form.audit_date });
            message.success('Audit team record saved');
            setModal(null);
            load();
        } catch { message.error('Save failed'); }
        finally { setSaving(false); }
    };

    const handleDelete = (id) => {
        Modal.confirm({
            title: 'Delete this audit team record?',
            
            onOk: async () => {
                const team = teams.find(t => t.id === id);
                await QoCService.deleteAuditTeam(id);
                trailDelete(TRAIL_MODULES.AUDIT_TEAM, team?.facility_name || String(id), id,
                    { period: team?.period_label, date: team?.audit_date });
                message.success('Deleted');
                load();
            }
        });
    };

    const cols = [
        {
            title:'Facility', width:180, ellipsis:true,
            render:(_,r) => (
                <div>
                    <div style={{ fontWeight:700, fontSize:12 }}>{r.facility_name}</div>
                    <div style={{ fontSize:10, color:'#888' }}>{r.site_type}</div>
                </div>
            )
        },
        {
            title:'Audit Date', width:110,
            render:(_,r) => r.audit_date
                ? <span style={{ fontSize:12 }}>{moment(r.audit_date).format('DD MMM YYYY')}</span>
                : '—'
        },
        {
            title:'Lead Auditor', width:140,
            render:(_,r) => (
                <span style={{ fontSize:12, fontWeight:600 }}>
                    <UserOutlined style={{ marginRight:4, color:'#1890ff' }}/>
                    {r.lead_auditor_name || r.lead_name || '—'}
                </span>
            )
        },
        {
            title:'Team', width:200,
            render:(_,r) => {
                const members = r.members || [];
                if (!members.length) return <span style={{ color:'#ccc' }}>—</span>;
                return (
                    <div style={{ display:'flex', flexWrap:'wrap', gap:3 }}>
                        {members.slice(0,3).map((m,i) => (
                            <Tag key={i} style={{ fontSize:9 }}>{m.name}</Tag>
                        ))}
                        {members.length > 3 && (
                            <Tag style={{ fontSize:9, color:'#888' }}>+{members.length-3}</Tag>
                        )}
                    </div>
                );
            }
        },
        {
            title:'Depts Visited', width:200,
            render:(_,r) => {
                const depts = r.departments_visited || [];
                return depts.length
                    ? <span style={{ fontSize:10, color:'#555' }}>{depts.slice(0,4).join(', ')}{depts.length>4?'...':''}</span>
                    : <span style={{ color:'#ccc' }}>—</span>;
            }
        },
        {
            title:'Period', width:100,
            render:(_,r) => r.period_label
                ? <Tag style={{ fontSize:10 }}>{r.period_label}</Tag>
                : '—'
        },
        {
            title:'', width:90, fixed:'right',
            render:(_,r) => (
                <div style={{ display:'flex', gap:4 }}>
                    <Button size="small" icon={<EditOutlined/>} onClick={() => openModal(r)}/>
                    <Button size="small" icon={<DeleteOutlined/>} danger onClick={() => handleDelete(r.id)}/>
                </div>
            )
        }
    ];

    return (
        <div className="page-content">
            <div className="page-head uc d-flex">
                <div className="my-auto">
                    <h2>Audit Team Registry</h2>
                    <div style={{ fontSize:12, color:'#888', marginTop:2 }}>
                        Record audit team composition, departments visited and meeting notes
                    </div>
                </div>
                <div className="my-auto ml-auto" style={{ display:'flex', gap:8 }}>
                    <Button icon={<ReloadOutlined/>} onClick={load} loading={loading}>Refresh</Button>
                    <Button type="primary" icon={<PlusOutlined/>} onClick={() => openModal(null)}>
                        Record Audit Team
                    </Button>
                </div>
            </div>

            <div className="page-pad">
                <Card size="small" style={{ marginBottom:14 }}>
                    <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                        <Select allowClear placeholder="All Periods" style={{ width:160 }}
                            value={filters.period_label || undefined}
                            onChange={v => setFilters(f => ({ ...f, period_label:v||'' }))}>
                            {periods.map(p => (
                                <Option key={p.label} value={p.label}>
                                    {parseInt(p.is_current)===1 ? '★ ' : ''}{p.label}
                                </Option>
                            ))}
                        </Select>
                        <Select allowClear placeholder="All Facilities" style={{ width:220 }}
                            showSearch optionFilterProp="children"
                            value={filters.facility_id || undefined}
                            onChange={v => setFilters(f => ({ ...f, facility_id:v||'' }))}>
                            {facilities.map(f => <Option key={f.id} value={f.id}>{f.business_name}</Option>)}
                        </Select>
                    </div>
                </Card>

                <Card size="small">
                    <Table columns={cols} dataSource={teams} rowKey="id"
                        loading={loading} scroll={{ x:1000 }}
                        pagination={{ pageSize:20, showTotal:t=>`${t} records` }}/>
                </Card>
            </div>

            {modal !== null && (
                <Modal open onCancel={() => setModal(null)}
                    title={<span><TeamOutlined style={{ marginRight:8 }}/>
                        {form.id ? 'Edit' : 'Record'} Audit Team</span>}
                    onOk={handleSave} okText="Save" confirmLoading={saving}
                    width={800} style={{ top:20 }}>
                    <Row gutter={12}>
                        <Col span={12}>
                            <label style={{ fontWeight:700, display:'block', marginBottom:4 }}>Facility *</label>
                            <Select showSearch optionFilterProp="children" style={{ width:'100%' }}
                                value={form.facility_id || undefined}
                                onChange={v => setForm(f => ({ ...f, facility_id:v }))}>
                                {facilities.map(f => <Option key={f.id} value={f.id}>{f.business_name}</Option>)}
                            </Select>
                        </Col>
                        <Col span={6}>
                            <label style={{ fontWeight:700, display:'block', marginBottom:4 }}>Audit Date *</label>
                            <DatePicker style={{ width:'100%' }}
                                value={form.audit_date ? moment(form.audit_date) : null}
                                onChange={v => setForm(f => ({ ...f, audit_date: v ? v.format('YYYY-MM-DD') : null }))}/>
                        </Col>
                        <Col span={6}>
                            <label style={{ fontWeight:700, display:'block', marginBottom:4 }}>Period</label>
                            <Select style={{ width:'100%' }} value={form.period_label || undefined}
                                onChange={v => setForm(f => ({ ...f, period_label:v }))}>
                                {periods.map(p => <Option key={p.label} value={p.label}>{p.label}</Option>)}
                            </Select>
                        </Col>

                        <Col span={24} style={{ marginTop:14 }}>
                            <Divider style={{ margin:'4px 0 10px' }}>
                                <TeamOutlined/> Audit Team Members
                            </Divider>
                            {(form.members||[]).map((m, i) => (
                                <Row key={i} gutter={8} style={{ marginBottom:8, alignItems:'center' }}>
                                    <Col span={8}>
                                        <Select showSearch optionFilterProp="children"
                                            placeholder="Select user" style={{ width:'100%' }}
                                            value={m.user_id || undefined}
                                            onChange={v => updateMember(i,'user_id',v)}>
                                            {users.map(u => <Option key={u.id} value={u.id}>{u.name}</Option>)}
                                        </Select>
                                    </Col>
                                    <Col span={8}>
                                        <Input placeholder="Name (if not in system)"
                                            value={m.name||''}
                                            onChange={e => updateMember(i,'name',e.target.value)}/>
                                    </Col>
                                    <Col span={6}>
                                        <Select style={{ width:'100%' }} value={m.role||'Auditor'}
                                            onChange={v => updateMember(i,'role',v)}>
                                            {ROLES.map(r => <Option key={r} value={r}>{r}</Option>)}
                                        </Select>
                                    </Col>
                                    <Col span={2}>
                                        <Button size="small" danger icon={<DeleteOutlined/>}
                                            onClick={() => removeMember(i)}/>
                                    </Col>
                                </Row>
                            ))}
                            <Button size="small" icon={<PlusOutlined/>} onClick={addMember}
                                style={{ marginTop:4 }}>
                                Add Member
                            </Button>
                        </Col>

                        <Col span={24} style={{ marginTop:14 }}>
                            <Divider style={{ margin:'4px 0 10px' }}>Departments Visited</Divider>
                            <Checkbox.Group
                                style={{ display:'flex', flexWrap:'wrap', gap:6 }}
                                value={form.departments_visited || []}
                                onChange={v => setForm(f => ({ ...f, departments_visited:v }))}>
                                {DEPTS.map(d => (
                                    <Checkbox key={d} value={d}
                                        style={{ fontSize:11, margin:0 }}>
                                        {d}
                                    </Checkbox>
                                ))}
                            </Checkbox.Group>
                        </Col>

                        <Col span={12} style={{ marginTop:14 }}>
                            <label style={{ fontWeight:700, display:'block', marginBottom:4 }}>Opening Meeting Notes</label>
                            <TextArea rows={3} value={form.opening_meeting_notes||''}
                                onChange={e => setForm(f => ({ ...f, opening_meeting_notes:e.target.value }))}
                                placeholder="Attendees, key discussions at opening meeting..."/>
                        </Col>
                        <Col span={12} style={{ marginTop:14 }}>
                            <label style={{ fontWeight:700, display:'block', marginBottom:4 }}>Closing Meeting Notes</label>
                            <TextArea rows={3} value={form.closing_meeting_notes||''}
                                onChange={e => setForm(f => ({ ...f, closing_meeting_notes:e.target.value }))}
                                placeholder="Summary of findings presented, facility responses..."/>
                        </Col>

                        <Col span={24} style={{ marginTop:10 }}>
                            <label style={{ fontWeight:700, display:'block', marginBottom:4 }}>External Observers</label>
                            <Input value={form.observer_names||''}
                                onChange={e => setForm(f => ({ ...f, observer_names:e.target.value }))}
                                placeholder="Names of any observers (WHO, NGO partners, etc.)"/>
                        </Col>
                    </Row>
                </Modal>
            )}
        </div>
    );
}


